import { GAME_CONFIG, type GameConfig, type PremiumOfferConfig } from "./gameConfig.ts";

/** Publication candidate. Existing season snapshots must never be rewritten. */
export const SEASON_TWO_CODE = "diamond-kingdom-season-2-v1";
export const SEASON_TWO_TITLE = "Сезон 2 — Дороги королевства";
const hour = 3600;

export const SEASON_TWO_PREMIUM: readonly PremiumOfferConfig[] = [
  { id: "tap-boost-50-2h", kind: "tap-boost", priceStars: 140, startsAt: null, endsAt: null, purchaseLimit: 12, durationSeconds: 2 * hour, multiplierBasisPoints: 15000, featured: true },
  { id: "passive-boost-50-12h", kind: "passive-boost", priceStars: 180, startsAt: null, endsAt: null, purchaseLimit: 12, durationSeconds: 12 * hour, multiplierBasisPoints: 15000, featured: true },
  { id: "passive-boost-50-24h", kind: "passive-boost", priceStars: 280, startsAt: null, endsAt: null, purchaseLimit: 8, durationSeconds: 24 * hour, multiplierBasisPoints: 15000, featured: false },
  { id: "energy-recovery-50-12h", kind: "energy-recovery-boost", priceStars: 120, startsAt: null, endsAt: null, purchaseLimit: 12, durationSeconds: 12 * hour, multiplierBasisPoints: 15000, featured: true },
  { id: "energy-recovery-50-24h", kind: "energy-recovery-boost", priceStars: 200, startsAt: null, endsAt: null, purchaseLimit: 8, durationSeconds: 24 * hour, multiplierBasisPoints: 15000, featured: false },
];

export const SEASON_TWO_CONFIG: GameConfig = {
  ...GAME_CONFIG,
  version: "season-two-caravans-2026-10-02",
  progress: { ...GAME_CONFIG.progress, achievements: GAME_CONFIG.progress.achievements.map(a => a.id === "season-one-participant" ? { ...a, id: "season-two-participant" as const } : a) },
  season: { ...GAME_CONFIG.season, title: SEASON_TWO_TITLE },
  upgrades: {
    precisionCutter: { ...GAME_CONFIG.upgrades.precisionCutter, levels: [
      ...GAME_CONFIG.upgrades.precisionCutter.levels,
      { level: 15, effectValue: "330", costToReach: "25000000" },
      { level: 16, effectValue: "400", costToReach: "45000000" },
      { level: 17, effectValue: "480", costToReach: "80000000" },
      { level: 18, effectValue: "570", costToReach: "140000000" },
    ] },
    mechanicalCutter: { ...GAME_CONFIG.upgrades.mechanicalCutter, levels: [
      ...GAME_CONFIG.upgrades.mechanicalCutter.levels,
      { level: 15, effectValue: "430000", costToReach: "28000000" },
      { level: 16, effectValue: "520000", costToReach: "50000000" },
      { level: 17, effectValue: "620000", costToReach: "90000000" },
      { level: 18, effectValue: "730000", costToReach: "160000000" },
    ] },
    energyCapacity: { ...GAME_CONFIG.upgrades.energyCapacity, levels: [
      ...GAME_CONFIG.upgrades.energyCapacity.levels,
      { level: 10, effectValue: "6800", costToReach: "15000000" },
      { level: 11, effectValue: "8600", costToReach: "28000000" },
      { level: 12, effectValue: "10800", costToReach: "50000000" },
    ] },
    energyRecovery: { ...GAME_CONFIG.upgrades.energyRecovery, levels: [
      ...GAME_CONFIG.upgrades.energyRecovery.levels,
      { level: 10, effectValue: "2", costToReach: "28000000" },
      { level: 11, effectValue: "1", costToReach: "60000000" },
    ] },
  },
  liveOps: {
    ...GAME_CONFIG.liveOps,
    daily: { ...GAME_CONFIG.liveOps.daily, tasks: [
      ...GAME_CONFIG.liveOps.daily.tasks,
      { code: "tap-royal-shift", contentKey: "tap-royal-shift", compatibilityGroup: "rhythm", audience: { minSeasonDay: 7, minConfirmedTaps: "30000" }, metric: "confirmed-taps", target: "1800", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
      { code: "energy-royal-cycle", contentKey: "energy-royal-cycle", compatibilityGroup: "rhythm", audience: { minSeasonDay: 10, minConfirmedTaps: "40000" }, metric: "energy-spent", target: "1400", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
      { code: "primary-royal-order", contentKey: "primary-royal-order", compatibilityGroup: "production", audience: { minSeasonDay: 7, minPrimaryProduced: "3000000" }, metric: "primary-produced", target: "150000", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
      { code: "primary-grand-caravan", contentKey: "primary-grand-caravan", compatibilityGroup: "production", audience: { minSeasonDay: 12, minPrimaryProduced: "15000000" }, metric: "primary-produced", target: "500000", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
      { code: "passive-royal-stream", contentKey: "passive-royal-stream", compatibilityGroup: "production", audience: { minSeasonDay: 7, minMechanicalLevel: 8 }, metric: "passive-produced", target: "100000", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
      { code: "passive-grand-reserve", contentKey: "passive-grand-reserve", compatibilityGroup: "production", audience: { minSeasonDay: 14, minMechanicalLevel: 10 }, metric: "passive-produced", target: "300000", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
      { code: "arena-royal-series", contentKey: "arena-royal-series", compatibilityGroup: "arena", audience: { minSeasonDay: 7, minArenaMatches: 20 }, metric: "arena-matches", target: "5", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
      { code: "arena-double-victory", contentKey: "arena-double-victory", compatibilityGroup: "arena", audience: { minSeasonDay: 14, minArenaMatches: 40 }, metric: "arena-wins", target: "2", reward: "0", enabled: true, startsAt: null, endsAt: null, retroactive: false },
    ] },
  },
  premium: { ...GAME_CONFIG.premium, offers: [...GAME_CONFIG.premium.offers, ...SEASON_TWO_PREMIUM] },
};

export type CaravanBoostKind = "tap-boost" | "passive-boost" | "energy-recovery-boost";
export type CaravanBoostReward = { kind: CaravanBoostKind; basisPoints: number; durationSeconds: number };
/** Each confirmed launch counts towards exactly one issued task. */
export const SEASON_TWO_CARAVAN_REWARDS: readonly {
  planId: number; nominalUsdtMicros: string; rewards: readonly CaravanBoostReward[];
}[] = [
  { planId: 1, nominalUsdtMicros: "20000000", rewards: [
    { kind: "tap-boost", basisPoints: 5000, durationSeconds: 12 * hour },
  ] },
  { planId: 2, nominalUsdtMicros: "50000000", rewards: [
    { kind: "tap-boost", basisPoints: 5000, durationSeconds: 24 * hour },
    { kind: "energy-recovery-boost", basisPoints: 5000, durationSeconds: 12 * hour },
  ] },
  { planId: 3, nominalUsdtMicros: "100000000", rewards: [
    { kind: "tap-boost", basisPoints: 5000, durationSeconds: 24 * hour },
    { kind: "passive-boost", basisPoints: 5000, durationSeconds: 24 * hour },
    { kind: "energy-recovery-boost", basisPoints: 5000, durationSeconds: 24 * hour },
  ] },
];
